import React from "react";
import { Link } from "react-router-dom";
import { MePageTitle, MeSection, MeStagger } from "./MeUI";
import { useCustomerCms } from "../../lib/useCustomerCms";
import {
  Baby, PersonSimple, UserCircle, User, PawPrint, HandSoap,
  BellRinging, Pill, Syringe, HandHeart, ClipboardText, HouseSimple,
  CaretRight, Egg, Drop, Sparkle,
} from "@phosphor-icons/react";

/**
 * /me/care — Daily Care hub
 *   • Baby Care
 *   • Elder Care
 *   • Women Care
 *   • Kids Care
 *   • Pet Care
 *   • Care Reminders (vaccines, pills, home inventory)
 */

const CARE_AREAS = [
  { key: "area_baby",     to: "/me/health/baby",     icon: Baby,        title: "Baby care",  desc: "Kicks, feeds, growth charts", tone: "pink" },
  { key: "area_elder",    to: "/me/health/elder",    icon: UserCircle,  title: "Elder care", desc: "Vitals, meds, safety", tone: "amber" },
  { key: "area_women",    to: "/me/health/cycle",    icon: Drop,        title: "Women care", desc: "Cycle, PMS, ovulation", tone: "red" },
  { key: "area_pregnancy",to: "/me/health/pregnancy",icon: Egg,         title: "Pregnancy",  desc: "Week-by-week guide", tone: "rose" },
  { key: "area_kids",     to: "/me/health/kids",     icon: PersonSimple,title: "Kids care",  desc: "Habits, milestones", tone: "blue" },
  { key: "area_pet",      to: "/me/health/pet",      icon: PawPrint,    title: "Pet care",   desc: "Vaccines, food, walks", tone: "green" },
];

const REMINDERS = [
  { key: "rem_vaccines", to: "/me/health/vaccines",  icon: Syringe,      title: "Vaccine reminders", desc: "Auto-scheduled IAP calendar" },
  { key: "rem_pills",    to: "/me/health/pills",     icon: Pill,         title: "Pill reminders",    desc: "Never miss a dose" },
  { key: "rem_vitals",   to: "/me/health/vitals",    icon: HandHeart,    title: "Vitals tracker",    desc: "BP, sugar, weight, SpO₂" },
  { key: "rem_growth",   to: "/me/health/growth",    icon: ClipboardText,title: "Growth charts",     desc: "WHO percentiles" },
  { key: "rem_inventory",to: "/me/health/inventory", icon: HouseSimple,  title: "Home inventory",    desc: "Medicines & essentials" },
  { key: "rem_circle",   to: "/me/health/circle",    icon: HandSoap,     title: "Care circle",       desc: "Loved ones you look after" },
];

export default function MeDailyCareHub() {
  const cms = useCustomerCms();
  const areas = cms.orderFeatures("care",
    CARE_AREAS.filter(a => cms.isFeatureVisible("care", a.key)), (x) => x.key);
  const rems = cms.orderFeatures("care",
    REMINDERS.filter(r => cms.isFeatureVisible("care", r.key)), (x) => x.key);
  return (
    <div className="font-body pb-6" data-testid="me-daily-care">
      <MePageTitle
        eyebrow="Daily Care"
        title={<>Care for everyone,<br/><span className="text-[#DC2626]">every day</span></>}
        subtitle="Track loved ones and get gentle reminders that never miss a dose."
      />

      <MeStagger stagger={40}>
        {/* Care areas */}
        {areas.length > 0 && (
        <MeSection className="mt-2">
          <SectionHead icon={HandHeart} title="Care areas" note="Tap a profile to open its dashboard" />
          <div className="grid grid-cols-2 gap-3 mt-3">
            {areas.map(a => <AreaTile key={a.to} {...a} />)}
          </div>
        </MeSection>
        )}

        {/* Reminders */}
        {rems.length > 0 && (
        <MeSection className="mt-6">
          <SectionHead icon={BellRinging} title="Care reminders" note="Everything you can automate" />
          <div className="mt-3 space-y-2">
            {rems.map(r => <ReminderRow key={r.to} {...r} />)}
          </div>
        </MeSection>
        )}

        {/* Motivational strip */}
        <MeSection className="mt-6">
          <div className="rounded-2xl bg-gradient-to-br from-[#FEF7F6] to-[#FDECEA] border border-[#F4C7B9] p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white shrink-0 flex items-center justify-center">
              <Sparkle size={20} weight="fill" className="text-[#DC2626]"/>
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-[#111827]">Small habits, big lives</div>
              <div className="text-[11px] text-[#5C5A55] leading-snug mt-0.5">
                We remind you gently, so caring for your family feels effortless.
              </div>
            </div>
          </div>
        </MeSection>
      </MeStagger>
    </div>
  );
}

// ─── locally shared bits ──────────────────────────────────
const SectionHead = ({ icon: Icon, title, note }) => (
  <div className="flex items-center gap-2">
    <div className="w-9 h-9 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
      <Icon size={18} weight="fill"/>
    </div>
    <div className="min-w-0">
      <div className="font-display font-black text-[16px] text-[#111827] leading-none">{title}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{note}</div>
    </div>
  </div>
);

const TONE = {
  red:   "from-[#DC2626] to-[#B91C1C]",
  rose:  "from-[#E11D48] to-[#9F1239]",
  pink:  "from-[#DB2777] to-[#9D174D]",
  blue:  "from-[#1E3A8A] to-[#1E40AF]",
  amber: "from-[#B45309] to-[#92400E]",
  green: "from-[#15803D] to-[#166534]",
};
const AreaTile = ({ to, icon: Icon, title, desc, tone }) => (
  <Link
    to={to}
    data-testid={`care-area-${title.toLowerCase().replace(/\s+/g,"-")}`}
    className="relative rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-[0_6px_20px_rgba(0,0,0,0.04)] p-3 active:scale-[0.98] transition"
  >
    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${TONE[tone] || TONE.red} text-white flex items-center justify-center`}>
      <Icon size={20} weight="fill"/>
    </div>
    <div className="mt-3 text-[14px] font-bold text-[#111827] leading-tight">{title}</div>
    <div className="text-[11px] text-gray-500 mt-0.5">{desc}</div>
  </Link>
);

const ReminderRow = ({ to, icon: Icon, title, desc }) => (
  <Link
    to={to}
    data-testid={`care-rem-${title.toLowerCase().replace(/\s+/g,"-")}`}
    className="flex items-center gap-3 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_14px_rgba(0,0,0,0.03)] px-3 py-3 active:scale-[0.995] transition"
  >
    <div className="w-11 h-11 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
      <Icon size={20} weight="fill"/>
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[14px] font-bold text-[#111827] leading-tight">{title}</div>
      <div className="text-[11px] text-gray-500 mt-0.5 truncate">{desc}</div>
    </div>
    <CaretRight size={14} className="text-gray-400 shrink-0"/>
  </Link>
);
